import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { combineLatest, map, take } from 'rxjs';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { StringFilterListComponent } from 'src/app/shared/components/string-filter-list/string-filter-list.component';
import { TemplatesQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { Paginable } from 'src/app/shared/types/paginable';
import { OrderTypeEnum } from '../../models/new/order';
import { ShotType, ShotTypeFilters } from '../../models/new/shot';
import { CreateShotTypeInput, DeleteShotTypeInput, UpdateShotTypeInput } from '../../models/types/shot-type-input.type';
import { ShotTypeMapper } from '../../services/shot-type-mapper';
import { ShotTypeModalComponent } from './shot-type-modal/shot-type-modal.component';

@Component({
  standalone: false,
  selector: 'bt-company-shot-types-list',
  templateUrl: './company-shot-types-list.component.html',
  styleUrls: ['./company-shot-types-list.component.scss']
})
export class CompanyShotTypesListComponent implements OnInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #graphQlService = inject(GraphQLService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #utilitiesService = inject(UtilitiesService);
  readonly #shotTypeMapper = inject(ShotTypeMapper);
  readonly #dialog = inject(MatDialog);

  shotTypes: Paginable<ShotType> = new Paginable<ShotType>();
  shotTypeFilters = new ShotTypeFilters();
  loading = false;

  ngOnInit(): void {
    this.#route.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((params: Params) => {
      this.shotTypeFilters = this.#shotTypeMapper.urlToFilters(params);
      this.fetchShotTypes();
    });
  }

  fetchShotTypes() {
    this.loading = true;
    this.shotTypes.currentPage = this.shotTypeFilters.page || 1;
    this.shotTypes.size = this.shotTypeFilters.size || 20;

    const pagination = this.shotTypes.buildQuery(this.shotTypes.currentPage);

    combineLatest([
      this.#graphQlService.getData(TemplatesQueries.GET_SHOT_TYPE_LIST, {
        where: this.#shotTypeMapper.toWhereClause(this.shotTypeFilters),
        ...pagination
      }),
      this.#graphQlService.getData(TemplatesQueries.GET_LEGACY_SHOT_TYPE_CODES)
    ])
      .pipe(
        take(1),
        takeUntilDestroyed(this.#destroyRef),
        map(([res, legacyRes]: any[]) => {
          const shotTypes = res.data.shotTypes;
          const legacyCodes = this.#shotTypeMapper.toLegacyCodesByShotType(
            legacyRes.data.exportCodifiedUseMaps?.nodes || []
          );

          return new Paginable<ShotType>({
            items: (shotTypes.nodes || []).map((node: any) => {
              const shotType = this.#shotTypeMapper.toShotType(node);
              shotType.legacyCodes = legacyCodes.get(node.id) || [];
              return shotType;
            }),
            totalCount: shotTypes.totalCount,
            currentPage: this.shotTypes.currentPage,
            size: this.shotTypes.size
          });
        })
      )
      .subscribe({
        next: (data: Paginable<ShotType>) => {
          this.shotTypes = data;
          this.loading = false;
        },
        error: (err) => {
          this.#utilitiesService.showSnackbar('Error fetching shot types', SnackbarType.Error);
          this.loading = false;
          console.error(err);
        }
      });
  }

  openCreateShotTypeModal() {
    this.#dialog
      .open(ShotTypeModalComponent)
      .afterClosed()
      .subscribe((input?: CreateShotTypeInput) => {
        if (input) {
          this.saveShotType(
            MutationQuery.CREATE_SHOT_TYPE,
            'createShotType',
            input,
            'Shot type created successfully',
            'Error creating shot type'
          );
        }
      });
  }

  openEditShotTypeModal(shotType: ShotType) {
    this.#dialog
      .open(ShotTypeModalComponent, { data: { shotType } })
      .afterClosed()
      .subscribe((input?: UpdateShotTypeInput) => {
        if (input) {
          this.saveShotType(
            MutationQuery.UPDATE_SHOT_TYPE,
            'updateShotType',
            input,
            'Shot type updated successfully',
            'Error updating shot type'
          );
        }
      });
  }

  deleteShotType(shotType: ShotType) {
    if (!shotType.id || shotType.isInUse) {
      return;
    }

    this.#dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove shot type',
          message: `Are you sure you want to remove "${shotType.name}"?`
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        const input: DeleteShotTypeInput = { shotTypeId: shotType.id! };
        this.saveShotType(
          MutationQuery.DELETE_SHOT_TYPE,
          'deleteShotType',
          input,
          'Shot type removed successfully',
          'Error removing shot type'
        );
      });
  }

  private saveShotType(
    mutation: MutationQuery,
    responseKey: string,
    input: CreateShotTypeInput | UpdateShotTypeInput | DeleteShotTypeInput,
    successMessage: string,
    errorMessage: string
  ) {
    this.loading = true;
    this.#graphQlService
      .postData(mutation, { input })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (res: any) => {
          const payload = res?.data?.[responseKey];
          if (payload?.success === false) {
            this.loading = false;
            this.#utilitiesService.showSnackbar(payload.message || errorMessage, SnackbarType.Error);
            return;
          }

          this.#utilitiesService.showSnackbar(payload?.message || successMessage, SnackbarType.Success);
          this.fetchShotTypes();
        },
        error: (err) => {
          this.loading = false;
          this.#utilitiesService.showSnackbar(
            err?.networkError?.error?.errors?.[0]?.message || errorMessage,
            SnackbarType.Error
          );
        }
      });
  }

  getLegacyCodesLabel(shotType: ShotType): string {
    return shotType.legacyCodes?.length ? shotType.legacyCodes.join(', ') : '-';
  }

  goLegacyExportTemplate() {
    this.#router.navigate(['/company/third-parties-applications']);
  }

  getOrderTypeLabel(shotType: ShotType): string {
    switch (shotType.orderType) {
      case OrderTypeEnum.StillLife:
        return 'Still-Life';
      case OrderTypeEnum.Dressed:
        return 'On Model';
      case OrderTypeEnum.Complete:
        return 'Complete';
      default:
        return '-';
    }
  }

  openNameFilterModal() {
    this.#dialog
      .open(StringFilterListComponent, {
        data: { title: 'Name', selected: this.shotTypeFilters.name }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            this.shotTypeFilters.name = res;
            this.shotTypeFilters.page = 1;
            this.refreshNavigation();
          }
        }
      });
  }

  cleanFilter(filter: string) {
    switch (filter) {
      case 'name':
        this.shotTypeFilters.name = undefined;
        break;
    }

    this.shotTypeFilters.page = 1;
    this.refreshNavigation();
  }

  pageEvent(event: { pageNumber: number; pageSize: number }) {
    this.shotTypeFilters.page = event.pageNumber;
    this.shotTypeFilters.size = event.pageSize;
    this.refreshNavigation();
  }

  refreshNavigation() {
    this.loading = true;
    this.#router
      .navigate([location.pathname], {
        queryParams: this.#shotTypeMapper.filtersToUrl(this.shotTypeFilters)
      })
      .then((navigated) => {
        // Stessi filtri = stesso URL: il router ignora la navigazione e la lista non si ricarica
        if (!navigated) {
          this.loading = false;
        }
      });
  }
}
